// src/pages/store/EditMeta.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Typography,
  Box,
  Paper,
} from "@mui/material";
import { toast } from "react-hot-toast";

import PageHeader from "../../../components/page-layout/PageHeader";
import FormikTextField from "../../../components/form/FormikTextField";
import axiosInstance from "../../../utils/axiosInstance";
import { storeMetaUrl } from "../../../utils/urls";
import { showLoader, hideLoader } from "../../../store/loaderSlice";
import { errorHandler } from "../../../utils/errorHandler";

const validationSchema = Yup.object({
  acceptanceLimit: Yup.number()
    .min(10, "Must be equal or more than 10")
    .required("Acceptance Limit is required"),
  rejectLimit: Yup.number()
    .min(0, "Must be zero or more")
    .required("Reject Limit is required"),
  rejectLimitAfterAccept: Yup.number()
    .min(0, "Must be equal or more than 0")
    .required("Reject Limit (After Accept) is required"),
  requestRejectCount: Yup.number()
    .min(0, "Must be zero or more")
    .required("Request Reject Count is required"),
  acceptRequestRejectCount: Yup.number()
    .min(0, "Must be zero or more")
    .required("Accept Request Reject Count is required"),
  rejectPenaltyAmount: Yup.number()
    .min(0, "Must be zero or more")
    .required("Reject Penalty Amount is required"),
  acceptedRejectPenaltyAmount: Yup.number()
    .min(0, "Must be zero or more")
    .required("Accepted Reject Penalty Amount is required"),
});

export default function EditMeta() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { storeId } = useParams();
  const storeName = location.state?.name || "Store";

  const [initialValues, setInitialValues] = useState({
    acceptanceLimit: "",
    rejectLimit: "",
    rejectLimitAfterAccept: "",
    requestRejectCount: "",
    acceptRequestRejectCount: "",
    rejectPenaltyAmount: "",
    acceptedRejectPenaltyAmount: "",
  });

  const [storeMetaId, setStoreMetaId] = useState(null);

  // Fetch existing meta
  useEffect(() => {
    dispatch(showLoader("Fetching store meta information..."));
    axiosInstance
      .get(`${storeMetaUrl}/${storeId}`)
      .then(({ data: meta }) => {
        setInitialValues({
          acceptanceLimit: meta.accept_limit ?? "",
          rejectLimit: meta.request_reject_limit ?? "",
          rejectLimitAfterAccept: meta.request_accepted_reject_limit ?? "",
          requestRejectCount: meta.request_reject_count ?? "",
          acceptRequestRejectCount: meta.accept_request_reject_count ?? "",
          rejectPenaltyAmount: meta.reject_penalty_amount ?? "",
          acceptedRejectPenaltyAmount: meta.accepted_reject_penalty_amount ?? "",
        });
        setStoreMetaId(meta.id);
      })
      .catch(errorHandler)
      .finally(() => dispatch(hideLoader()));
  }, [dispatch, storeId]);

  return (
    <Box>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          if (!storeMetaId) {
            toast.error("Meta ID not loaded. Please refresh the page.");
            return;
          }

          dispatch(showLoader("Updating meta information..."));

          const payload = {
            accept_limit: parseInt(values.acceptanceLimit, 10),
            request_reject_limit: parseInt(values.rejectLimit, 10),
            request_accepted_reject_limit: parseInt(values.rejectLimitAfterAccept, 10),
            request_reject_count: parseInt(values.requestRejectCount, 10),
            accept_request_reject_count: parseInt(values.acceptRequestRejectCount, 10),
            reject_penalty_amount: parseInt(values.rejectPenaltyAmount, 10),
            accepted_reject_penalty_amount: parseInt(values.acceptedRejectPenaltyAmount, 10),
          };

          axiosInstance
            .patch(`${storeMetaUrl}/${storeMetaId}`, payload)
            .then(() => {
              toast.success("Meta information updated successfully!");
              navigate(-1);
            })
            .catch(errorHandler)
            .finally(() => {
              dispatch(hideLoader());
              setSubmitting(false);
            });
        }}
      >
        {({ handleSubmit, dirty, isValid }) => (
          <Form>
            <PageHeader
              title={`${storeName} — Meta Configuration`}
              lead="Adjust acceptance/rejection limits for this store."
              showBack
              buttonTitle="Save Changes"
              buttonOnClick={handleSubmit}
              buttonDisabled={!dirty || !isValid}
            />

            <Paper elevation={2} sx={{ p: 4, borderRadius: 4, mb: 4 }}>
              <Grid container spacing={4} alignItems="center">
                {/* Acceptance Limit */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Acceptance Limit
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 2, mt: 1, mb: 2, color: "grey.600" }}
                  >
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Shows how many requests are accepted at a time.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Must be equal or more than 10.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="acceptanceLimit"
                    placeholder="e.g. 10"
                    fullWidth
                  />
                </Grid>

                {/* Reject Limit */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Reject Limit
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 2, mt: 1, mb: 2, color: "grey.600" }}
                  >
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Number of requests you can reject directly.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Must be zero or more.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="rejectLimit"
                    placeholder="e.g. 0"
                    fullWidth
                  />
                </Grid>

                {/* Reject Limit (After Accept) */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Reject Limit (After Accept)
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 2, mt: 1, mb: 2, color: "grey.600" }}
                  >
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Reject limit after you've accepted a request.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Must be zero or more.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="rejectLimitAfterAccept"
                    placeholder="e.g. 0"
                    fullWidth
                  />
                </Grid>

                {/* Request Reject Count */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Request Reject Count
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 2, mt: 1, mb: 2, color: "grey.600" }}
                  >
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Total rejections before accepting requests.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Must be zero or more.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="requestRejectCount"
                    placeholder="e.g. 0"
                    fullWidth
                  />
                </Grid>

                {/* Accept Request Reject Count */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Accept Request Reject Count
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 2, mt: 1, mb: 2, color: "grey.600" }}
                  >
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Total rejections after accepting requests.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Must be zero or more.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="acceptRequestRejectCount"
                    placeholder="e.g. 0"
                    fullWidth
                  />
                </Grid>

                {/* Reject Penalty Amount */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Reject Penalty Amount
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 2, mt: 1, mb: 2, color: "grey.600" }}
                  >
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Penalty amount per rejection before acceptance.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Must be zero or more.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="rejectPenaltyAmount"
                    placeholder="e.g. 5"
                    fullWidth
                  />
                </Grid>

                {/* Accepted Reject Penalty Amount */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Accepted Reject Penalty Amount
                  </Typography>
                  <Box
                    component="ul"
                    sx={{ pl: 2, mt: 1, mb: 2, color: "grey.600" }}
                  >
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Penalty amount per rejection after acceptance.
                    </Typography>
                    <Typography component="li" variant="body2" sx={{ fontSize: '0.875rem' }}>
                      Must be zero or more.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="acceptedRejectPenaltyAmount"
                    placeholder="e.g. 10"
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Paper>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
